import { ArrowDown, ArrowUp, Check, ListFilter, Plus, Settings, Trash2 } from 'lucide-react';
import { type RefObject, type TouchEvent, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { TaskArrangementView, TaskGroupBy, TaskSort } from '../task-workspace-model.ts';
import { TASK_GROUP_OPTIONS, TASK_SORT_OPTIONS } from './options.ts';
import { IconToolbarButton, useToolbarPopoverPosition } from './toolbar.tsx';

/** Popover for switching between built-in and custom task arrangements. */
export function TaskArrangementPopover({
  triggerRef,
  scope,
  onClose,
  activeArrangementId,
  builtInArrangements,
  customArrangements,
  onSelect,
  onCreate,
  onManage,
}: {
  triggerRef: RefObject<HTMLButtonElement>;
  scope: 'mobile' | 'desktop';
  onClose(): void;
  activeArrangementId: string;
  builtInArrangements: TaskArrangementView[];
  customArrangements: TaskArrangementView[];
  onSelect(arrangementId: string): void;
  onCreate(): void;
  onManage(): void;
}): JSX.Element {
  const menuRef = useRef<HTMLDivElement>(null);
  const style = useToolbarPopoverPosition({
    align: 'right',
    menuRef,
    onClose,
    open: true,
    triggerRef,
    width: 320,
  });

  if (typeof document === 'undefined') return <></>;

  return createPortal(
    <div
      ref={menuRef}
      data-testid="task-arrangement-popover"
      data-scope={scope}
      data-arrangement-popover-scope={scope}
      onTouchStart={(event) => event.stopPropagation()}
      onTouchEnd={(event) => event.stopPropagation()}
      style={style}
      className="fixed z-[1000] rounded-lg border border-[color:var(--app-border)] bg-[color:var(--app-panel)] p-2 shadow-lg"
    >
      <ArrangementSection title="Simple views">
        {builtInArrangements.map((arrangement) => (
          <ArrangementOption
            key={arrangement.id}
            arrangement={arrangement}
            active={arrangement.id === activeArrangementId}
            onSelect={() => onSelect(arrangement.id)}
          />
        ))}
      </ArrangementSection>
      <ArrangementSection title="Advanced views">
        {customArrangements.length === 0 ? (
          <p className="surface-empty p-3 text-xs">No advanced views yet.</p>
        ) : (
          customArrangements.map((arrangement) => (
            <ArrangementOption
              key={arrangement.id}
              arrangement={arrangement}
              active={arrangement.id === activeArrangementId}
              onSelect={() => onSelect(arrangement.id)}
            />
          ))
        )}
      </ArrangementSection>
      <div className="mt-2 grid grid-cols-2 gap-2 border-t border-[color:var(--app-border)] pt-2">
        <button type="button" onClick={onCreate} className="ui-button h-9 gap-2 px-3 text-xs">
          <Plus aria-hidden="true" size={14} />
          New view
        </button>
        <button type="button" onClick={onManage} className="ui-button h-9 gap-2 px-3 text-xs">
          <Settings aria-hidden="true" size={14} />
          Manage
        </button>
      </div>
    </div>,
    document.body,
  );
}

function ArrangementSection({
  title,
  children,
}: {
  title: string;
  children: JSX.Element | JSX.Element[];
}): JSX.Element {
  return (
    <section className="mb-2 last:mb-0">
      <p className="eyebrow-label px-2 py-1">{title}</p>
      <div className="space-y-1">{children}</div>
    </section>
  );
}

function ArrangementOption({
  arrangement,
  active,
  onSelect,
}: {
  arrangement: TaskArrangementView;
  active: boolean;
  onSelect(): void;
}): JSX.Element {
  const handleTouchEnd = (event: TouchEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    onSelect();
  };

  return (
    <button
      type="button"
      data-testid={`task-arrangement-option-${arrangement.id}`}
      onClick={onSelect}
      onTouchStart={(event) => event.stopPropagation()}
      onTouchEnd={handleTouchEnd}
      className={`flex w-full items-start gap-2 rounded-md px-2 py-2 text-left transition ${
        active
          ? 'bg-[color:var(--app-accent-soft)] text-[color:var(--app-text)]'
          : 'text-[color:var(--app-text-muted)] hover:bg-white/70 hover:text-[color:var(--app-text)]'
      }`}
    >
      <ListFilter aria-hidden="true" size={15} className="mt-0.5 shrink-0" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{arrangement.name}</span>
        <span className="block truncate text-xs">{describeArrangement(arrangement)}</span>
      </span>
      {active && <Check aria-hidden="true" size={14} className="mt-0.5 shrink-0" />}
    </button>
  );
}

export interface ArrangementDraft {
  name: string;
  groupBy: TaskGroupBy;
  groupDirection: TaskSort['direction'];
  sort: TaskSort[];
}

const EMPTY_ARRANGEMENT_DRAFT: ArrangementDraft = {
  name: '',
  groupBy: 'project',
  groupDirection: 'asc',
  sort: [{ key: 'due', direction: 'asc' }],
};

/** Modal for creating, editing, and deleting custom task arrangements. */
export function TaskArrangementManagerDialog({
  arrangements,
  editingArrangementId,
  onEditingArrangementChange,
  onCreate,
  onUpdate,
  onDelete,
  onClose,
}: {
  arrangements: TaskArrangementView[];
  editingArrangementId: string | undefined;
  onEditingArrangementChange(id: string | undefined): void;
  onCreate(input: ArrangementDraft): void;
  onUpdate(id: string, patch: ArrangementDraft): void;
  onDelete(id: string): void;
  onClose(): void;
}): JSX.Element {
  const editingArrangement = arrangements.find(
    (arrangement) => arrangement.id === editingArrangementId,
  );
  const [draft, setDraft] = useState<ArrangementDraft>(() =>
    editingArrangement ? arrangementToDraft(editingArrangement) : EMPTY_ARRANGEMENT_DRAFT,
  );

  useEffect(() => {
    setDraft(editingArrangement ? arrangementToDraft(editingArrangement) : EMPTY_ARRANGEMENT_DRAFT);
  }, [editingArrangement]);

  const canSave = draft.name.trim().length > 0 && draft.sort.length > 0;

  const updateSort = (index: number, patch: Partial<TaskSort>): void => {
    setDraft((current) => ({
      ...current,
      sort: current.sort.map((rule, ruleIndex) =>
        ruleIndex === index ? { ...rule, ...patch } : rule,
      ),
    }));
  };

  const moveSortRule = (index: number, delta: 1 | -1): void => {
    setDraft((current) => {
      const nextIndex = index + delta;
      if (nextIndex < 0 || nextIndex >= current.sort.length) return current;
      const nextSort = [...current.sort];
      const [rule] = nextSort.splice(index, 1);
      if (!rule) return current;
      nextSort.splice(nextIndex, 0, rule);
      return { ...current, sort: nextSort };
    });
  };

  const removeSortRule = (index: number): void => {
    setDraft((current) => ({
      ...current,
      sort:
        current.sort.length === 1
          ? current.sort
          : current.sort.filter((_, ruleIndex) => ruleIndex !== index),
    }));
  };

  return (
    <div className="mobile-safe-overlay fixed inset-0 z-50 flex items-center justify-center bg-[color:var(--app-modal-overlay)]">
      <div
        data-testid="task-arrangement-manager"
        className="grid max-h-[min(46rem,92vh)] w-[min(56rem,96vw)] grid-cols-[16rem_minmax(0,1fr)] overflow-hidden rounded-lg border border-[color:var(--app-border)] bg-[color:var(--app-panel)] shadow-xl max-md:grid-cols-1"
      >
        <aside className="min-h-0 border-r border-[color:var(--app-border)] p-3 max-md:border-b max-md:border-r-0">
          <div className="flex items-center justify-between gap-2">
            <p className="eyebrow-label">Advanced views</p>
            <button
              type="button"
              aria-label="Create advanced view"
              onClick={() => onEditingArrangementChange(undefined)}
              className="ui-button ui-button-subtle h-8 w-8 px-0"
            >
              <Plus aria-hidden="true" size={14} />
            </button>
          </div>
          <div className="mt-3 max-h-80 space-y-1 overflow-auto pr-1">
            {arrangements.length === 0 ? (
              <p className="surface-empty p-3 text-xs">Create your first advanced view.</p>
            ) : (
              arrangements.map((arrangement) => (
                <button
                  key={arrangement.id}
                  type="button"
                  onClick={() => onEditingArrangementChange(arrangement.id)}
                  className={`w-full rounded-md px-3 py-2 text-left text-sm transition ${
                    arrangement.id === editingArrangementId
                      ? 'bg-[color:var(--app-accent-soft)] text-[color:var(--app-text)]'
                      : 'text-[color:var(--app-text-muted)] hover:bg-white/70 hover:text-[color:var(--app-text)]'
                  }`}
                >
                  <span className="block truncate font-medium">{arrangement.name}</span>
                  <span className="block truncate text-xs">{describeArrangement(arrangement)}</span>
                </button>
              ))
            )}
          </div>
        </aside>
        <section className="min-h-0 overflow-auto p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="eyebrow-label">{editingArrangement ? 'Edit view' : 'New view'}</p>
              <h2 className="mt-1 text-lg font-semibold text-[color:var(--app-text)]">
                Task arrangement
              </h2>
            </div>
            <button type="button" onClick={onClose} className="ui-button ui-button-subtle">
              Close
            </button>
          </div>

          <div className="mt-5 grid gap-4">
            <label className="grid gap-2 text-sm font-medium text-[color:var(--app-text)]">
              Name
              <input
                data-testid="task-arrangement-name-input"
                value={draft.name}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, name: event.target.value }))
                }
                className="ui-input"
              />
            </label>

            <div className="grid grid-cols-[minmax(0,1fr)_9rem] gap-3 max-sm:grid-cols-1">
              <label className="grid gap-2 text-sm font-medium text-[color:var(--app-text)]">
                Group by
                <select
                  data-testid="task-arrangement-group-by"
                  value={draft.groupBy}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      groupBy: event.target.value as TaskGroupBy,
                    }))
                  }
                  className="ui-select"
                >
                  {TASK_GROUP_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-2 text-sm font-medium text-[color:var(--app-text)]">
                Group order
                <select
                  value={draft.groupDirection}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      groupDirection: event.target.value as TaskSort['direction'],
                    }))
                  }
                  className="ui-select"
                >
                  <option value="asc">Asc</option>
                  <option value="desc">Desc</option>
                </select>
              </label>
            </div>

            <div>
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-[color:var(--app-text)]">Sort priority</p>
                <button
                  type="button"
                  onClick={() =>
                    setDraft((current) => ({
                      ...current,
                      sort: [...current.sort, { key: 'due', direction: 'asc' }],
                    }))
                  }
                  className="ui-button h-8 gap-2 px-3 text-xs"
                >
                  <Plus aria-hidden="true" size={14} />
                  Add sort
                </button>
              </div>
              <div className="mt-2 space-y-2">
                {draft.sort.map((rule, index) => (
                  <div
                    key={`${rule.key}-${index}`}
                    className="grid grid-cols-[minmax(0,1fr)_8rem_repeat(3,2rem)] items-end gap-2"
                  >
                    <label className="grid gap-1 text-xs font-medium text-[color:var(--app-text-muted)]">
                      Field
                      <select
                        data-testid={`task-arrangement-sort-key-${index}`}
                        value={rule.key}
                        onChange={(event) =>
                          updateSort(index, { key: event.target.value as TaskSort['key'] })
                        }
                        className="ui-select h-9 py-1.5 text-xs"
                      >
                        {TASK_SORT_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="grid gap-1 text-xs font-medium text-[color:var(--app-text-muted)]">
                      Order
                      <select
                        value={rule.direction}
                        onChange={(event) =>
                          updateSort(index, {
                            direction: event.target.value as TaskSort['direction'],
                          })
                        }
                        className="ui-select h-9 py-1.5 text-xs"
                      >
                        <option value="asc">Asc</option>
                        <option value="desc">Desc</option>
                      </select>
                    </label>
                    <IconToolbarButton
                      label="Move sort earlier"
                      disabled={index === 0}
                      onClick={() => moveSortRule(index, -1)}
                      subtle
                    >
                      <ArrowUp aria-hidden="true" size={14} />
                    </IconToolbarButton>
                    <IconToolbarButton
                      label="Move sort later"
                      disabled={index === draft.sort.length - 1}
                      onClick={() => moveSortRule(index, 1)}
                      subtle
                    >
                      <ArrowDown aria-hidden="true" size={14} />
                    </IconToolbarButton>
                    <IconToolbarButton
                      label="Remove sort"
                      disabled={draft.sort.length === 1}
                      onClick={() => removeSortRule(index)}
                      subtle
                    >
                      <Trash2 aria-hidden="true" size={14} />
                    </IconToolbarButton>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-[color:var(--app-border)] pt-4">
            <button
              type="button"
              disabled={!canSave}
              onClick={() => {
                if (editingArrangement) onUpdate(editingArrangement.id, draft);
                else onCreate(draft);
              }}
              className="ui-button ui-button-primary"
            >
              {editingArrangement ? 'Save view' : 'Create view'}
            </button>
            {editingArrangement && (
              <button
                type="button"
                onClick={() => onDelete(editingArrangement.id)}
                className="ui-button ui-button-subtle gap-2"
              >
                <Trash2 aria-hidden="true" size={14} />
                Delete
              </button>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function arrangementToDraft(arrangement: TaskArrangementView): ArrangementDraft {
  return {
    name: arrangement.name,
    groupBy: arrangement.groupBy,
    groupDirection: arrangement.groupDirection,
    sort:
      arrangement.sort.length > 0
        ? arrangement.sort.map((rule) => ({ ...rule }))
        : [{ key: 'due', direction: 'asc' }],
  };
}

function describeArrangement(arrangement: TaskArrangementView): string {
  const group =
    TASK_GROUP_OPTIONS.find((option) => option.value === arrangement.groupBy)?.label ?? 'Project';
  const sort = arrangement.sort
    .map((rule) => {
      const label =
        TASK_SORT_OPTIONS.find((option) => option.value === rule.key)?.label ?? rule.key;
      return `${label} ${rule.direction}`;
    })
    .join(', ');
  return `Group: ${group}; Sort: ${sort || 'Default'}`;
}
